import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect, useRef } from 'react'
import { AuthProvider } from './context/AuthContext'
import { ThemeProvider } from './context/ThemeContext'
import api from './api/axios'
import { analyticsPromise } from './utils/firebase'
import { logEvent } from 'firebase/analytics'

function ScrollToTop() {
  const { pathname } = useLocation()
  useEffect(() => { window.scrollTo(0, 0) }, [pathname])
  return null
}

function PageTracker() {
  const { pathname } = useLocation()
  const lastTracked = useRef(null)
  useEffect(() => {
    if (lastTracked.current === pathname) return
    lastTracked.current = pathname
    api.post(`/analytics/pageview?path=${encodeURIComponent(pathname)}`).catch(() => {})
    analyticsPromise.then(a => { if (a) logEvent(a, 'page_view', { page_path: pathname }) })
  }, [pathname])
  return null
}

import ProtectedRoute from './components/ProtectedRoute'
import Navbar from './components/Navbar'
import Footer from './components/Footer'
import GlobalSidebars from './components/GlobalSidebars'
import MobileBottomAds from './components/MobileBottomAds'
import SupportWidget from './components/SupportWidget'
import MobileAppNudge from './components/MobileAppNudge'
import InstallPromptDaily from './components/InstallPromptDaily'
import ConsentGate from './components/ConsentGate'

// Critical path — eager
import Jobs from './pages/Jobs'

// Lazy — loaded on demand
const Login           = lazy(() => import('./pages/Login'))
const Register        = lazy(() => import('./pages/Register'))
const JobDetail       = lazy(() => import('./pages/JobDetail'))
const PostJob         = lazy(() => import('./pages/PostJob'))
const MyJobs          = lazy(() => import('./pages/MyJobs'))
const MyApplications  = lazy(() => import('./pages/MyApplications'))
const JobApplications = lazy(() => import('./pages/JobApplications'))
const Profile         = lazy(() => import('./pages/Profile'))
const Settings        = lazy(() => import('./pages/Settings'))
const WorkerProfiles       = lazy(() => import('./pages/WorkerProfiles'))
const WorkerProfilesBrowse = lazy(() => import('./pages/WorkerProfilesBrowse'))
const PublicProfile   = lazy(() => import('./pages/PublicProfile'))
const Messages        = lazy(() => import('./pages/Messages'))
const ConversationDetail = lazy(() => import('./pages/ConversationDetail'))
const AdPage          = lazy(() => import('./pages/AdPage'))
const AdvertisePage   = lazy(() => import('./pages/AdvertisePage'))
const About           = lazy(() => import('./pages/About'))
const Contact         = lazy(() => import('./pages/Contact'))
const PrivacyPolicy   = lazy(() => import('./pages/PrivacyPolicy'))
const Terms           = lazy(() => import('./pages/Terms'))
const Partnerships    = lazy(() => import('./pages/Partnerships'))

function PageFallback() {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div className="spinner" />
    </div>
  )
}

export default function App() {
  return (
    <ThemeProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <PageTracker />
        <Navbar />
        <GlobalSidebars />

        <main id="main-content">
        <Suspense fallback={<PageFallback />}>
          <Routes>
            {/* Public */}
            <Route path="/"          element={<Jobs />} />
            <Route path="/jobs/:id"  element={<JobDetail />} />
            <Route path="/login"     element={<Login />} />
            <Route path="/register"  element={<Register />} />
            <Route path="/users/:handle" element={<PublicProfile />} />

            {/* Reklam */}
            <Route path="/ads/:id"   element={<AdPage />} />
            <Route path="/advertise" element={<AdvertisePage />} />

            {/* Məlumat səhifələri */}
            <Route path="/about"        element={<About />} />
            <Route path="/contact"      element={<Contact />} />
            <Route path="/privacy"      element={<PrivacyPolicy />} />
            <Route path="/terms"        element={<Terms />} />
            <Route path="/partnerships" element={<Partnerships />} />

            {/* Authenticated */}
            <Route path="/profile"  element={<ProtectedRoute><Profile /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />

            {/* Employer */}
            <Route path="/post-job"                 element={<ProtectedRoute role="EMPLOYER"><PostJob /></ProtectedRoute>} />
            <Route path="/my-jobs"                  element={<ProtectedRoute role="EMPLOYER"><MyJobs /></ProtectedRoute>} />
            <Route path="/my-jobs/:id/applications" element={<ProtectedRoute role="EMPLOYER"><JobApplications /></ProtectedRoute>} />

            {/* Worker */}
            <Route path="/my-applications" element={<ProtectedRoute role="WORKER"><MyApplications /></ProtectedRoute>} />
            <Route path="/worker-profiles" element={<ProtectedRoute role="WORKER"><WorkerProfiles /></ProtectedRoute>} />

            {/* İşçi profilləri — hamıya açıq (qonaqlar daxil) */}
            <Route path="/workers" element={<WorkerProfilesBrowse />} />

            {/* Messaging */}
            <Route path="/messages"     element={<ProtectedRoute><Messages /></ProtectedRoute>} />
            <Route path="/messages/:id" element={<ProtectedRoute><ConversationDetail /></ProtectedRoute>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
        </main>

        <MobileBottomAds />
        <SupportWidget />
        <MobileAppNudge />
        <InstallPromptDaily />
        <ConsentGate />
        <Footer />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  )
}
